import React from 'react'
import { Link, useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import Fuse from 'fuse.js'
import { useHotkeys } from 'react-hotkeys-hook'
import styled from 'styled-components'
import { useQuery } from 'react-query'
import { COLORS } from 'COLORS'
import { SearchIcon } from 'icons/SearchIcon/SearchIcon'
import { createPortal } from 'react-dom'
import { defaultFontFamily } from 'helpers/font'
import { showSidebarItem } from 'helpers/sidebar'
import { buildMenuItems } from 'helpers/menu_item_builder'
import {
  SIDEBAR_SEARCH_RESULTS,
  STATIC_SEARCH_RESULTS
} from 'constants/global_search'
import { Icon } from '@junglescout/edna'

import { useTranslation } from 'react-i18next'
import { GlobalSearchEmptyState } from './GlobalSearchEmptyState'
import { GlobalSearchNoResultsState } from './GlobalSearchNoResultsState'

const SEARCH_RESULT_HEIGHT = 72

const GlobalSearchWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 48px;
`

const SearchBait = styled.div`
  margin-right: 24px;
  border: 1px solid ${COLORS.coolGrey300};
  border-radius: 4px;
  display: flex;
  padding: 4px 8px;
  width: 390px;
  margin-top: 3px;
  position: relative;
  cursor: pointer;
  align-items: center;
`
const GlobalSearchModalContainer = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  z-index: 300000;
`

const GlobalSearchModalBackdrop = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
  background: ${COLORS.blackOpacity20};
`

const GlobalSearchModalDialog = styled.div`
  position: absolute;
  top: -1;
  z-index: 1000;
  width: 520px;
  height: 500px;
  background: white;
  box-shadow: 0 4px 10px 0 ${COLORS.blackOpacity20};
  border: solid 1px ${COLORS.coolGrey300};
  border-radius: 4px;
`

const GlobalSearchModalDialogSearchBox = styled.div`
  border-bottom: 1px solid ${COLORS.coolGrey300};
  display: flex;
  align-items: center;
`

const EmptyStateWrapper = styled.div`
  & > * {
    border: none;
  }
`

const Chip = styled.span`
  ${props =>
    !props.expanded &&
    `
    border: 1px solid #4563EC;
    font-size: 10px;
    color: #4563EC;
    display: inline-block;
    margin: 0 8px;
    border-radius: 2px;
    padding: 1px 2px;
    background: #fff;
  `}
`

const SearchBaitInput = styled.input`
  ${props =>
    !props.expanded &&
    `
    font-family: ${defaultFontFamily(props.lang)};
    font-size: 12px;
    line-height: 1.5;
    color: ${COLORS.grey900};
    height: 24px;
    padding: 4px 8px;
    outline: none;
    border: none;
    cursor: pointer;
    background: none;
    flex: 1;
  `}
`

const SearchInput = styled.input`
  ${props =>
    !props.expanded &&
    `
    font-family: ${defaultFontFamily(props.lang)};
    width: 100%;
    line-height: 1.5;
    color: ${COLORS.grey900};
    height: 48px;
    border: none;
    padding: 8px 16px;
    outline: none;
  `}
`

const SearchResultList = styled.ul`
  padding: 0;
  height: 440px;
  overflow-y: auto;
`

const SearchResultListItem = styled.li`
  list-style: none;
  display: block;
`

const SearchResultListItemIconWrapper = styled.div`
  width: 40px;
  display: flex;
  align-items: center;
`

const SearchResultListItemBody = styled.div`
  ${props =>
    `
    display: flex;
    flex-direction: row;
    flex: 1;
    height: ${SEARCH_RESULT_HEIGHT}px;
    box-sizing: border-box;
    padding: 16px;
    display: flex;
    cursor: pointer;
    background: ${props.isSelected ? `${COLORS.blue100}` : 'none'}
    `}
`

const SearchResultListItemContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
`

const SearchResultListItemTitle = styled.span`
  cursor: pointer;
  display: -webkit-box;
  ${props =>
    !props.expanded &&
    `
      text-overflow: ellipsis;
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      color: ${COLORS.blue500}
  `}
`

const StyledExternalLink = styled.a`
  cursor: pointer;
`

const StyledLink = styled(Link)`
  cursor: pointer;
`

const SearchResultListItemDescription = styled.p`
  margin: 0;
  text-overflow: ellipsis;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
`

const getAllowedSideBarSearchResults = (globalData, t) => {
  const params = {
    admin: globalData.employeeWithAccess,
    appType: globalData.appType,
    flagData: globalData.flagData,
    membershipInfo: globalData.membershipInfo,
    permissions: globalData.user.permissions
  }
  const allowedSidebarMenuItems = buildMenuItems({ t })
    .filter(item => {
      return showSidebarItem({
        item,
        ...params
      })
    })
    .reduce((a, b) => {
      if (b.children?.length) {
        return a.concat(b.children)
      }
      return a.concat([b])
    }, [])

  const builtList = SIDEBAR_SEARCH_RESULTS(t).filter(item => {
    return allowedSidebarMenuItems.some(
      allowedItem => allowedItem.url === item.url
    )
  })

  return builtList
}

const ASIN_REGEX = '([Bb][\\da-zA-Z]{9}|\\d{9}(X|\\d))'
const validationAsinRegex = new RegExp(`^${ASIN_REGEX}$`)

export const GlobalSearchComponent = () => {
  const [isDialogOpen, setIsDialogOpen] = React.useState(false)
  const [searchText, setSearchText] = React.useState('')
  const [searchResults, setSearchResults] = React.useState([])
  const [selectedIndex, setSelectedIndex] = React.useState(0)

  const searchResultListElement = React.useRef(null)

  const membershipInfo = useSelector(state => state.globalData?.membershipInfo)

  const globalData = useSelector(state => state.globalData)

  const isCobalt = membershipInfo?.code === 'COBALT'
  const { data: userData } = useQuery(`/api/v1/search_index/user_data`, {
    enabled: isCobalt,
    refetchOnMount: true
  })

  const history = useHistory()

  const { t } = useTranslation([
    'sidebar',
    'global_search',
    'generic',
    'constants'
  ])

  const fuse = React.useMemo(() => {
    const allowedSideBarSearchResults = getAllowedSideBarSearchResults(
      globalData,
      t
    )

    const securityTrimmedStaticSearchResults = STATIC_SEARCH_RESULTS(t).filter(
      result => {
        return result.canAccess(globalData) && result.withinLimits(globalData)
      }
    )
    let allSearchResults = [
      ...allowedSideBarSearchResults,
      ...securityTrimmedStaticSearchResults
    ]
    const mappedSearchResults = (userData || [])
      .map(searchResult => {
        switch (searchResult.type) {
          case 'segment': {
            const country = searchResult.metadata.country?.toUpperCase()
            const countrySection = country ? ` / ${country}` : ''
            const buildStatusSection = ` / Build status: ${searchResult.metadata.build_status
              .split('_')
              .join(' ')}`
            const asinsSection = ` / ${searchResult.metadata.total_asins} ASINs`

            return {
              type: 'segment',
              title: `${searchResult.title}`,
              description: `${
                searchResult.metadata.active ? 'Active' : 'Paused'
              } segment${countrySection}${buildStatusSection}${asinsSection}`,
              keywords: ['segment'],
              url: `/market-insights/share-trends/${searchResult.id}`,
              icon: <Icon name="NAV_MARKET_INSIGHT" />
            }
          }
          case 'user': {
            return globalData?.membershipInfo.admin === true
              ? {
                  type: 'user',
                  title: `${searchResult.title}`,
                  description: `${searchResult.metadata.email}`,
                  keywords: ['team', 'user'],
                  url: '/account/team-settings?tab=users',
                  icon: <Icon name="SETTINGS_COG" />
                }
              : null
          }
          default:
            return null
        }
      })
      .filter(searchResult => !!searchResult)
    allSearchResults = allSearchResults.concat(mappedSearchResults)
    return new Fuse(allSearchResults, {
      keys: ['title', 'description', 'keywords']
    })
  }, [globalData, t, userData])

  React.useEffect(() => {
    async function getSearchResults() {
      if (searchText) {
        const results = await fuse.search(searchText)
        setSearchResults(results)
      } else {
        setSearchResults([])
      }
    }
    getSearchResults()
  }, [fuse, searchText])

  const handleSearchTextChange = event => {
    setSearchText(event.target.value)
    setSelectedIndex(0)
  }

  useHotkeys('cmd+k,ctrl+k', () => setIsDialogOpen(true))

  const allSearchResults = [...searchResults]
  const looksLikeAsin = validationAsinRegex.test(searchText)
  if (looksLikeAsin) {
    allSearchResults.push({
      item: {
        title: `View '${searchText}' in ASIN Explorer`,
        url: `/product-insights/asin-explorer/us/${searchText}`,
        type: 'ASIN'
      }
    })
  }

  const handleSelectResult = searchResult => {
    setIsDialogOpen(false)
    if (searchResult.item.url.indexOf('/') === 0) {
      history.push(searchResult.item.url)
    } else {
      window.open(searchResult.item.url, '_blank').focus()
    }
    setSearchText('')
  }

  const handleKeyDown = event => {
    if (
      event.key === 'Escape' ||
      (event.key === 'k' && (event.ctrlKey || event.metaKey))
    ) {
      setIsDialogOpen(false)
      setSearchText('')
    } else if (event.key === 'ArrowDown') {
      if (selectedIndex < allSearchResults.length - 1) {
        setSelectedIndex(selectedIndex + 1)
      }
      searchResultListElement.current.scrollTo({
        top: SEARCH_RESULT_HEIGHT * (selectedIndex - 1)
      })
      event.preventDefault()
    } else if (event.key === 'ArrowUp') {
      if (selectedIndex > 0) {
        setSelectedIndex(selectedIndex - 1)
      }
      searchResultListElement.current.scrollTo({
        top: SEARCH_RESULT_HEIGHT * (selectedIndex - 1)
      })
      event.preventDefault()
    } else if (event.key === 'Enter') {
      if (allSearchResults.length > 0) {
        handleSelectResult(allSearchResults[selectedIndex])

        event.preventDefault()
      }
    }
  }

  const transferFocusFromSearchBaitToDialog = e => {
    setIsDialogOpen(true)
    e.currentTarget.blur()
  }

  return (
    <GlobalSearchWrapper>
      <SearchBait
        onClick={transferFocusFromSearchBaitToDialog}
        onFocus={transferFocusFromSearchBaitToDialog}>
        <SearchBaitInput
          type="text"
          name="global_search"
          placeholder="Search for content, features, ASINs, and settings"
        />
        <Chip>Beta</Chip>
        <SearchIcon size="16" />
      </SearchBait>

      {isDialogOpen &&
        createPortal(
          <GlobalSearchModalContainer>
            <GlobalSearchModalBackdrop onClick={() => setIsDialogOpen(false)} />

            <GlobalSearchModalDialog>
              <div>
                <GlobalSearchModalDialogSearchBox>
                  <SearchInput
                    type="text"
                    name="global_search"
                    value={searchText}
                    onChange={handleSearchTextChange}
                    autoFocus
                    placeholder="Search for content, features, ASINs, and settings"
                    onKeyDown={handleKeyDown}
                  />
                  <Chip>Beta</Chip>
                  <SearchResultListItemIconWrapper>
                    <SearchIcon size="18" />
                  </SearchResultListItemIconWrapper>
                </GlobalSearchModalDialogSearchBox>
                <div>
                  <EmptyStateWrapper>
                    {!searchText && <GlobalSearchEmptyState />}
                    {searchText && allSearchResults.length === 0 && (
                      <GlobalSearchNoResultsState />
                    )}
                  </EmptyStateWrapper>
                  {allSearchResults?.length > 0 && (
                    <SearchResultList ref={searchResultListElement}>
                      {allSearchResults.map((result, i) => {
                        const isSelected = i === selectedIndex
                        const ItemIcon = result.item.icon
                        const isObject = typeof ItemIcon === 'object'
                        const content = (
                          <SearchResultListItemBody isSelected={isSelected}>
                            <SearchResultListItemIconWrapper>
                              {ItemIcon && !isObject ? <ItemIcon /> : ItemIcon}
                            </SearchResultListItemIconWrapper>
                            <SearchResultListItemContent>
                              <SearchResultListItemTitle>
                                {result.item.title}
                              </SearchResultListItemTitle>
                              <SearchResultListItemDescription>
                                {result.item.description}
                              </SearchResultListItemDescription>
                            </SearchResultListItemContent>
                          </SearchResultListItemBody>
                        )
                        return (
                          <SearchResultListItem
                            key={result.item.title}
                            onMouseOver={() => setSelectedIndex(i)}
                            isSelected={isSelected}
                            onClick={() => {
                              setIsDialogOpen(false)
                            }}>
                            {result.item.url.indexOf('https://') === 0 ? (
                              <StyledExternalLink
                                href={result.item.url}
                                target="_blank"
                                rel="noopener noreferrer">
                                {content}
                              </StyledExternalLink>
                            ) : (
                              <StyledLink to={result.item.url}>
                                {content}
                              </StyledLink>
                            )}
                          </SearchResultListItem>
                        )
                      })}
                    </SearchResultList>
                  )}
                </div>
              </div>
            </GlobalSearchModalDialog>
          </GlobalSearchModalContainer>,
          document.body
        )}
    </GlobalSearchWrapper>
  )
}

export { GlobalSearchComponent as GlobalSearch }
